import React from "react";
import { X, Briefcase, Clock, AlertCircle, Plus, UploadCloud, CheckCircle2, Trash2, Loader2 } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import DatePicker from "../../../components/DatePicker";

const RequestModal = ({
  showRequestModal,
  setShowRequestModal,
  isPartialDayMode,
  setIsPartialDayMode,
  leaveForm,
  setLeaveForm,
  partialDayForm,
  setPartialDayForm,
  leaveTypes,
  leaveStats,
  isSubmitting,
  handleSubmitRequest,
  prescription,
  isUploadingPrescription,
  prescriptionError,
  handlePrescriptionUpload,
  handleRemovePrescription,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  language,
  t,
}) => {

  if (!showRequestModal) return null;

  const modalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    backdropFilter: "blur(10px)",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const modalContentStyle = {
    background: isDarkMode
      ? "rgba(15, 23, 42, 0.95)"
      : "rgba(248, 250, 252, 0.98)",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    border: isDarkMode
      ? "1px solid rgba(71, 84, 103, 0.3)"
      : "1px solid rgba(203, 213, 225, 0.2)",
    borderRadius:
      screenSize === "xs" ? "1.25rem" : screenSize === "sm" ? "1.25rem" : "1.5rem",
    padding: screenSize === "xs" ? "1.25rem" : screenSize === "sm" ? "1rem" : spacing.padding,
    width: screenSize === "xs" ? "92%" : screenSize === "sm" ? "92%" : "90%",
    maxWidth:
      screenSize === "xs" ? "24rem" : screenSize === "sm" ? "28rem" : "43.75rem",
    margin: screenSize === "xs" ? "1rem auto" : screenSize === "sm" ? "1rem auto" : "11.875rem auto",
    maxHeight: screenSize === "xs" ? "85vh" : screenSize === "sm" ? "85vh" : "80vh",
    overflowY: "auto",
    direction: language === "ar" ? "rtl" : "ltr",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const inputStyle = {
    width: "100%",
    padding: screenSize === "xs" ? "12px 16px" : "16px 20px",
    borderRadius: screenSize === "xs" ? "10px" : "14px",
    border: isDarkMode
      ? "2px solid rgba(71,84,103,0.5)"
      : "2px solid rgba(203,213,225,0.3)",
    background: isDarkMode ? "rgba(15,23,42,0.8)" : "rgba(248,250,252,0.95)",
    color: isDarkMode ? "#e2e8f0" : "#1e293b",
    fontSize: screenSize === "xs" ? "14px" : "15px",
    fontWeight: "500",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    fontFamily: "'Cairo', sans-serif",
    // Makes the native date picker icon/popup follow the modal theme.
    colorScheme: isDarkMode ? "dark" : "light",
  };

  const labelStyle = {
    display: "block",
    fontSize: screenSize === "xs" ? "13px" : "14px",
    fontWeight: "700",
    color: isDarkMode ? "#ffffff" : "#1a1a1a",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "'Cairo', sans-serif",
  };

  const isSubmitDisabled =
    isSubmitting ||
    isUploadingPrescription ||
    (isPartialDayMode
      ? !partialDayForm.date || !partialDayForm.halfDayType || !partialDayForm.reason
      : !leaveForm.type ||
        !leaveForm.startDate ||
        !leaveForm.endDate ||
        !leaveForm.reason ||
        (leaveForm.type === "sick" && !prescription?.path));

  return (
    <div style={modalStyle} onClick={() => setShowRequestModal(false)}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: screenSize === "xs" ? "12px" : "16px",
            marginBottom: screenSize === "xs" ? "16px" : screenSize === "sm" ? "16px" : "32px",
            paddingBottom: screenSize === "xs" ? "12px" : screenSize === "sm" ? "12px" : "24px",
            borderBottom: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: screenSize === "xs" ? "40px" : "48px",
              height: screenSize === "xs" ? "40px" : "48px",
              background: isPartialDayMode
                ? "linear-gradient(135deg, #8b5cf6, #8b5cf6)"
                : "linear-gradient(135deg, #2e7cba, #2e7cba)",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isPartialDayMode ? (
              <Clock size={screenSize === "xs" ? 20 : 24} color="#ffffff" />
            ) : (
              <Briefcase size={screenSize === "xs" ? 20 : 24} color="#ffffff" />
            )}
          </div>

          {/* Title */}
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: 0,
                color: isDarkMode ? "#ffffff" : "#1a1a1a",
                fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
                fontWeight: "700",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {isPartialDayMode ? t.leaveTypes.partialDay : t.request.title}
            </h2>
            <p
              style={{
                margin: "0.5rem 0 0 0",
                color: isDarkMode
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
                fontSize: screenSize === "xs" ? "0.875rem" : "1rem",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {isPartialDayMode
                ? t.leaveDescriptions.partialDay
                : t.pageSubtitle}
            </p>
          </div>

          {/* Close Button */}
          <button
            style={{
              background: isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
              border: "none",
              color: isDarkMode ? "white" : "#1a1a1a",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowRequestModal(false)}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* ===== PARTIAL DAY FORM ===== */}
        {isPartialDayMode ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: screenSize === "xs" ? "16px" : "24px" }}>
            {/* Date */}
            <div>
              <label style={labelStyle}>{t.request.date} *</label>
              <DatePicker
                theme={isDarkMode ? "dark" : "light"}
                isRTL={language === "ar"}
                value={partialDayForm.date}
                onChange={(v) => setPartialDayForm({ ...partialDayForm, date: v })}
                buttonStyle={inputStyle}
              />
            </div>

            {/* Annual Leave Summary */}
            {(() => {
              const statTotal = leaveStats.find((s) => s.label === t.stats.totalAvailable)?.value;
              const statUsed = leaveStats.find((s) => s.label === t.stats.daysUsed)?.value;
              const statLeft = leaveStats.find((s) => s.label === t.stats.daysRemaining)?.value;
              const annualLeave = leaveTypes.find((lt) => lt.id === "annual") || { available: 0, used: 0 };
              const annTotal = Number(statTotal ?? annualLeave.available ?? 0);
              const annUsed = Number(statUsed ?? annualLeave.used ?? 0);
              const annLeft = Number(statLeft ?? Math.max(annTotal - annUsed, 0));
              const percentUsed = annTotal ? Math.round((annUsed / annTotal) * 100) : 0;

              const mutedColor = isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)";

              return (
                <div
                  style={{
                    padding: screenSize === "xs" ? "12px" : "16px",
                    borderRadius: screenSize === "xs" ? "10px" : "14px",
                    border: isDarkMode ? "1px solid rgba(71,84,103,0.5)" : "1px solid rgba(203,213,225,0.6)",
                    background: isDarkMode ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)",
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "14px" }}>
                    {[
                      { label: t.balance.used, value: annUsed, color: "#8b5cf6" },
                      { label: t.balance.total, value: annTotal, color: isDarkMode ? "#e2e8f0" : "#1e293b" },
                      { label: t.balance.left, value: annLeft, color: "#10b981" },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem", fontWeight: 800, color, lineHeight: 1.2, fontFamily: "'Cairo', sans-serif" }}>{value}</div>
                        <div style={{ fontSize: "12px", color: mutedColor, marginTop: "2px", fontFamily: "'Cairo', sans-serif" }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ height: "6px", background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(2,6,23,0.08)", borderRadius: "999px", overflow: "hidden", marginBottom: "6px" }}>
                    <div style={{ width: `${Math.min(Math.max(percentUsed, 0), 100)}%`, height: "100%", background: "#8b5cf6", borderRadius: "999px" }} />
                  </div>
                  <div style={{ fontSize: "12px", color: mutedColor, fontFamily: "'Cairo', sans-serif" }}>
                    {percentUsed}% {t.stats.utilized}
                  </div>
                </div>
              );
            })()}

            {/* Half-Day Type */}
            <div>
              <label style={labelStyle}>{t.request.halfDayType} *</label>
              <ReusableDropdown
                items={[
                  { id: "first_half", label: t.request.firstHalf },
                  { id: "second_half", label: t.request.secondHalf },
                ]}
                value={partialDayForm.halfDayType}
                onChange={(value) => setPartialDayForm({ ...partialDayForm, halfDayType: value })}
                placeholder={t.request.selectHalfDayType}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={isDarkMode ? "dark" : "light"}
                language={language}
                inputStyle={{
                  fontWeight: "600",
                  fontSize: fonts.body,
                  background: isDarkMode ? "rgba(15,23,42,0.8)" : "rgba(248,250,252,0.95)",
                  border: isDarkMode ? "2px solid rgba(71,84,103,0.5)" : "2px solid rgba(203,213,225,0.3)",
                  color: isDarkMode ? "#e2e8f0" : "#1e293b",
                }}
              />
            </div>

            {/* Reason */}
            <div>
              <label style={labelStyle}>{t.request.reason} *</label>
              <textarea
                value={partialDayForm.reason}
                onChange={(e) =>
                  setPartialDayForm({ ...partialDayForm, reason: e.target.value })
                }
                placeholder={t.request.reasonPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>
          </div>
        ) : (
          // ===== REGULAR LEAVE FORM =====
          <div
            style={{
              display: "grid",
              gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
              gap: screenSize === "xs" ? "16px" : "24px",
            }}
          >
            {/* Leave Type */}
            <div style={{ gridColumn: screenSize === "xs" ? "1" : "1 / -1" }}>
              <label style={labelStyle}>{t.request.leaveType} *</label>
              <ReusableDropdown
                items={leaveTypes}
                value={
                  leaveForm.type
                    ? (() => {
                        const found = leaveTypes.find((lt) => lt.id === leaveForm.type);
                        return found ? found.id : "";
                      })()
                    : ""
                }
                onChange={(val) => {
                  const selected = leaveTypes.find((lt) => lt.id === val);
                  if (selected) {
                    if (selected.id === "partialDay") {
                      setIsPartialDayMode(true);
                      setPartialDayForm({ date: new Date().toISOString().split("T")[0], halfDayType: "", reason: "" });
                      setLeaveForm({ ...leaveForm, type: "partialDay", startDate: "", endDate: "", reason: "", handover: "" });
                    } else {
                      setIsPartialDayMode(false);
                      setLeaveForm({ ...leaveForm, type: selected.id });
                    }
                  }
                }}
                onSelect={(item) => {
                  if (item.id === "partialDay") {
                    setIsPartialDayMode(true);
                    setPartialDayForm({ date: new Date().toISOString().split("T")[0], halfDayType: "", reason: "" });
                    setLeaveForm({ ...leaveForm, type: "partialDay", startDate: "", endDate: "", reason: "", handover: "" });
                  } else {
                    setIsPartialDayMode(false);
                    setLeaveForm({ ...leaveForm, type: item.id });
                  }
                }}
                placeholder={t.request.selectType}
                getItemKey={(item) => item.id}
                getItemLabel={(item) =>
                  `${item.name} (${Math.max(item.available - item.used, 0)} ${t.request.daysAvailable})`
                }
                renderItem={(item) => (
                  <div style={{ flex: 1, textAlign: language === "ar" ? "right" : "left", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ fontWeight: 600, color: isDarkMode ? "#f1f5f9" : "#334155", fontSize: "15px" }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: "12px", color: isDarkMode ? "rgba(203,213,225,0.6)" : "#6b7280" }}>
                      {Math.max(item.available - item.used, 0)} {t.request.daysAvailable}
                    </div>
                  </div>
                )}
                theme={isDarkMode ? "dark" : "light"}
                language={language}
                maxItems={10}
                showAllOnArrowClick={true}
                showClear={true}
                onClear={() => setLeaveForm({ ...leaveForm, type: "" })}
                inputStyle={{
                  fontWeight: "600",
                  fontSize: fonts.body,
                  background: isDarkMode ? "rgba(15,23,42,0.8)" : "rgba(248,250,252,0.95)",
                  border: isDarkMode ? "2px solid rgba(71,84,103,0.5)" : "2px solid rgba(203,213,225,0.3)",
                  color: isDarkMode ? "#e2e8f0" : "#1e293b",
                }}
              />
            </div>

            {/* Start Date */}
            <div>
              <label style={labelStyle}>{t.request.startDate} *</label>
              <DatePicker
                theme={isDarkMode ? "dark" : "light"}
                isRTL={language === "ar"}
                value={leaveForm.startDate}
                onChange={(v) => setLeaveForm({ ...leaveForm, startDate: v })}
                buttonStyle={inputStyle}
              />
            </div>

            {/* End Date */}
            <div>
              <label style={labelStyle}>{t.request.endDate} *</label>
              <DatePicker
                theme={isDarkMode ? "dark" : "light"}
                isRTL={language === "ar"}
                value={leaveForm.endDate}
                onChange={(v) => setLeaveForm({ ...leaveForm, endDate: v })}
                minDate={leaveForm.startDate || undefined}
                buttonStyle={inputStyle}
              />
            </div>

            {/* Reason */}
            <div>
              <label style={labelStyle}>{t.request.reason} *</label>
              <textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder={t.request.reasonPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>

            {/* Handover */}
            <div>
              <label style={labelStyle}>{t.request.handover}</label>
              <textarea
                value={leaveForm.handover}
                onChange={(e) => setLeaveForm({ ...leaveForm, handover: e.target.value })}
                placeholder={t.request.handoverPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>

            {/* Prescription Upload (for Sick Leave) */}
            {leaveForm.type === "sick" && (
              <div style={{ gridColumn: screenSize === "xs" ? "1" : "1 / -1", marginTop: "8px" }}>
                <label style={labelStyle}>
                  {t.request.prescription} *
                </label>

                {prescription?.path ? (
                  /* Uploaded state card */
                  <div
                    style={{
                      padding: "16px 20px",
                      borderRadius: screenSize === "xs" ? "10px" : "14px",
                      border: isDarkMode ? "2px solid rgba(16, 185, 129, 0.4)" : "2px solid rgba(16, 185, 129, 0.3)",
                      background: isDarkMode ? "rgba(16, 185, 129, 0.1)" : "rgba(240, 253, 244, 0.95)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                      <CheckCircle2 size={24} color="#10b981" style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: isDarkMode ? "#34d399" : "#047857", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {prescription.fileName || "Prescription"}
                        </div>
                        <div style={{ fontSize: "12px", color: isDarkMode ? "rgba(255,255,255,0.6)" : "#64748b" }}>
                          {t.request.prescriptionUploaded}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePrescription}
                      style={{
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        color: "#ef4444",
                        padding: "8px 14px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                        flexShrink: 0,
                      }}
                    >
                      <Trash2 size={16} />
                      <span>{t.request.removePrescription}</span>
                    </button>
                  </div>
                ) : (
                  /* Upload input container */
                  <div>
                    <input
                      type="file"
                      id="prescription-file-input"
                      accept="image/*,application/pdf"
                      disabled={isUploadingPrescription}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handlePrescriptionUpload(file);
                        }
                      }}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="prescription-file-input"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        padding: "24px 20px",
                        borderRadius: screenSize === "xs" ? "10px" : "14px",
                        border: isDarkMode
                          ? "2px dashed rgba(71,84,103,0.6)"
                          : "2px dashed rgba(203,213,225,0.8)",
                        background: isDarkMode ? "rgba(15,23,42,0.6)" : "rgba(248,250,252,0.8)",
                        cursor: isUploadingPrescription ? "wait" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isUploadingPrescription ? (
                        <>
                          <Loader2 size={32} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6" }}>
                            {t.request.uploadingPrescription}
                          </span>
                        </>
                      ) : (
                        <>
                          <UploadCloud size={32} color={isDarkMode ? "#94a3b8" : "#64748b"} />
                          <div style={{ textAlign: "center" }}>
                            <span style={{ fontSize: "14px", fontWeight: "700", color: isDarkMode ? "#e2e8f0" : "#1e293b" }}>
                              {t.request.uploadPrescription || t.request.choosePrescription}
                            </span>
                            <div style={{ fontSize: "12px", color: isDarkMode ? "rgba(255,255,255,0.5)" : "#64748b", marginTop: "4px" }}>
                              {t.request.prescriptionHint}
                            </div>
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                )}

                {prescriptionError && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px 14px",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      borderRadius: "8px",
                      color: "#ef4444",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <AlertCircle size={16} />
                    <span>{prescriptionError}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Validation Errors */}
        {!isPartialDayMode && !leaveForm.type && leaveForm.reason && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.request.leaveType} {t.messages.selectDates}</span>
          </div>
        )}

        {!isPartialDayMode && leaveForm.startDate && leaveForm.endDate && new Date(leaveForm.startDate) > new Date(leaveForm.endDate) && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.messages.invalidDates}</span>
          </div>
        )}



        {isPartialDayMode && !partialDayForm.halfDayType && partialDayForm.reason && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.messages.selectHalfDayType}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: screenSize === "xs" ? "12px" : "16px",
            marginTop: screenSize === "sm" ? "16px" : "32px",
            paddingTop: screenSize === "sm" ? "12px" : "24px",
            borderTop: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            justifyContent: screenSize === "xs" ? "center" : "flex-end",
          }}
        >
          <button
            onClick={() => setShowRequestModal(false)}
            disabled={isSubmitting}
            style={{
              flex: screenSize === "xs" ? "1 1 48%" : "auto",
              padding: screenSize === "xs" ? "12px 16px" : "16px 24px",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              border: isDarkMode ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(0,0,0,0.1)",
              background: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
              color: isDarkMode ? "#ffffff" : "#1a1a1a",
              cursor: "pointer",
              fontSize: screenSize === "xs" ? "13px" : "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.request.cancel}
          </button>
          <button
            onClick={handleSubmitRequest}
            disabled={isSubmitDisabled}
            style={{
              flex: screenSize === "xs" ? "1 1 48%" : "auto",
              padding: screenSize === "xs" ? "12px 16px" : "16px 24px",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              border: "none",
              background: isSubmitDisabled
                ? "rgba(59, 130, 246, 0.5)"
                : "linear-gradient(135deg, #2e7cba, #2e7cba)",
              color: "#ffffff",
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              fontSize: screenSize === "xs" ? "13px" : "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {isSubmitting ? (
              <>
                <Clock size={16} style={{ animation: "spin 1s linear infinite" }} />
                {t.request.submitting}
              </>
            ) : (
              <>
                <Plus size={16} />
                {t.request.submitRequest}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestModal;